import { createContext, useContext, useEffect } from "react"
import type React from "react"

export type PageChrome = {
  title: string
  setTitle: (t: string) => void
  leftAction: React.ReactNode
  setLeftAction: (n: React.ReactNode) => void
  rightAction: React.ReactNode
  setRightAction: (n: React.ReactNode) => void
}

export const PageChromeContext = createContext<PageChrome | null>(null)

function usePageChrome(): PageChrome {
  const ctx = useContext(PageChromeContext)
  if (!ctx) {
    throw new Error("usePageChrome must be used inside <AppShell/>")
  }
  return ctx
}

/**
 * Set the navbar title for the current page. Mirrors the per-page title
 * the Jinja templates used to render in `.navbar h1` — each page owned
 * its own "🎵 TikTok Accounts" / "📊 Ads Manager" label.
 *
 * Pass an empty `title` to hide the navbar label. Optional `browserTitle`
 * keeps `document.title` set when the navbar title is blank.
 */
export function usePageTitle(title: string, browserTitle?: string): void {
  const { setTitle } = usePageChrome()
  const docTitle = browserTitle ?? title
  useEffect(() => {
    setTitle(title)
    if (docTitle) document.title = docTitle
    return () => {
      setTitle("Dashboard")
      document.title = "Dashboard"
    }
  }, [title, docTitle, setTitle])
}

/**
 * Render a node on the left side of the navbar, next to the burger
 * (e.g. page tabs). Cleared on unmount.
 */
export function useNavbarLeftAction(node: React.ReactNode): void {
  const { setLeftAction } = usePageChrome()
  useEffect(() => {
    setLeftAction(node)
    return () => setLeftAction(null)
  }, [node, setLeftAction])
}

/**
 * Render a node on the right side of the navbar (e.g. a page-scoped
 * "Refresh Balances" button). Cleared on unmount.
 */
export function useNavbarRightAction(node: React.ReactNode): void {
  const { setRightAction } = usePageChrome()
  useEffect(() => {
    setRightAction(node)
    return () => setRightAction(null)
  }, [node, setRightAction])
}
